/**
 * CoachPlaybook - PlayerCard Component
 * Version: 0.5.145
 * Description: Mobile-optimized player card with configurable field display
 * 
 * v0.5.119 Changes:
 * - Default jersey (isDefault) now displays on player cards
 * - Priority: player.jerseyTemplateId > isDefault > activeTemplateId > first template
 */

import React, { useState, useMemo } from 'react';
import { POSITIONS, calculateAverages } from '../../utils/players';
import JerseyIcon, { DEFAULT_JERSEY_TEMPLATE } from '../common/JerseyIcon';

const PlayerCard = ({ 
  player, 
  teamName = '', 
  jerseyTemplates = [], 
  activeTemplateId = 'default',
  onEdit, 
  onDelete, 
  fieldSettings = {} 
}) => {
  const [showActions, setShowActions] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  const averages = calculateAverages(player);

  // Default field settings if not provided
  const settings = {
    showHeight: true,
    showGrade: true,
    showGraduationYear: true,
    showNotes: true,
    ...fieldSettings
  };

  // Get jersey template from props (not IndexedDB)
  const jerseyTemplate = useMemo(() => {
    if (jerseyTemplates.length === 0) {
      return DEFAULT_JERSEY_TEMPLATE;
    }
    
    // If player has a specific template assigned, use it
    if (player.jerseyTemplateId) {
      const assigned = jerseyTemplates.find(t => t.id === player.jerseyTemplateId);
      if (assigned) return assigned;
    }
    
    // Prefer default jersey (isDefault), then activeTemplateId, then first
    const defaultJersey = jerseyTemplates.find(t => t.isDefault);
    if (defaultJersey) return defaultJersey;
    
    const active = jerseyTemplates.find(t => t.id === activeTemplateId) || jerseyTemplates[0];
    return active || DEFAULT_JERSEY_TEMPLATE;
  }, [jerseyTemplates, activeTemplateId, player.jerseyTemplateId]);

  const positionColors = {
    PG: 'bg-blue-100 text-blue-700 border-blue-300',
    SG: 'bg-green-100 text-green-700 border-green-300',
    SF: 'bg-yellow-100 text-yellow-700 border-yellow-300',
    PF: 'bg-orange-100 text-orange-700 border-orange-300',
    C: 'bg-purple-100 text-purple-700 border-purple-300'
  };

  // Swipe detection
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      setShowActions(true);
    }
    if (isRightSwipe) {
      setShowActions(false);
    }
  };

  return (
    <div data-element={`player-card-${player.id}`} className="relative overflow-hidden">
      <div
        data-element={`player-card-${player.id}-content`}
        className={`bg-slate-800 border border-slate-700 rounded-xl transition-transform duration-300 ${
          showActions ? '-translate-x-24 md:-translate-x-0' : 'translate-x-0'
        }`}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <div className="p-4 md:p-6">
          {/* Header */}
          <div className="flex items-start justify-between mb-3">
            {/* Jersey Icon */}
            <div data-element={`player-card-${player.id}-jersey`} className="flex-shrink-0 relative">
              <JerseyIcon 
                size={56}
                number={player.jerseyNumber}
                view="front"
                teamName={teamName}
                fillColor={jerseyTemplate.fillColor}
                outlineColor={jerseyTemplate.outlineColor}
                numberColor={jerseyTemplate.numberColor}
                numberOutline={jerseyTemplate.numberOutline}
                numberOutline2={jerseyTemplate.numberOutline2}
                textColor={jerseyTemplate.textColor}
                textOutline={jerseyTemplate.textOutline}
                textOutline2={jerseyTemplate.textOutline2}
                fontFamily={jerseyTemplate.fontFamily}
                numberY={jerseyTemplate.numberY ?? 55}
                numberSize={jerseyTemplate.numberSize ?? 100}
                nameY={jerseyTemplate.nameY ?? 5}
                nameSize={jerseyTemplate.nameSize ?? 100}
                jerseyBorderWeight={jerseyTemplate.jerseyBorderWeight ?? 12}
                numberBorderWeight={jerseyTemplate.numberBorderWeight ?? 6}
                numberBorderWeight2={jerseyTemplate.numberBorderWeight2 ?? 0}
                nameBorderWeight={jerseyTemplate.nameBorderWeight ?? 4}
                nameBorderWeight2={jerseyTemplate.nameBorderWeight2 ?? 0}
              />
              {/* Jersey Style Badge */}
              <div 
                data-element={`player-card-${player.id}-jersey-badge`}
                className="absolute -bottom-1 -right-1 px-1.5 py-0.5 text-[9px] font-bold rounded bg-slate-900 text-slate-300 shadow border border-slate-600"
                title={`Wearing: ${jerseyTemplate.name}`}
              >
                {jerseyTemplate.name?.slice(0, 4)}
              </div>
            </div>
            
            {/* Player Info */}
            <div data-element={`player-card-${player.id}-info`} className="flex-1 ml-4">
              <h3 data-element={`player-card-${player.id}-name`} className="text-lg font-bold text-white">
                {player.firstName} {player.lastName}
              </h3>
              <div className="flex flex-wrap items-center gap-2 mt-1">
                <span data-element={`player-card-${player.id}-position`} className={`px-2 py-0.5 text-xs font-semibold rounded border ${positionColors[player.position] || 'bg-slate-700 text-slate-300 border-slate-600'}`}>
                  {POSITIONS[player.position] || player.position}
                </span>
                {settings.showHeight && player.height && (
                  <span data-element={`player-card-${player.id}-height`} className="text-sm text-slate-400">{player.height}</span>
                )}
                {settings.showGrade && player.grade && (
                  <span data-element={`player-card-${player.id}-grade`} className="text-xs text-slate-400">{player.grade}</span>
                )}
              </div>
            </div>

            {/* Desktop Action Buttons */}
            <div data-element={`player-card-${player.id}-actions`} className="hidden md:flex gap-2 flex-shrink-0">
              <button
                data-element={`player-card-${player.id}-edit-btn`}
                onClick={() => onEdit(player)}
                className="p-2 text-slate-400 hover:text-blue-400 hover:bg-blue-900/30 rounded-lg transition-colors"
                title="Edit player"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
              </button>
              <button
                data-element={`player-card-${player.id}-delete-btn`}
                onClick={() => onDelete(player)}
                className="p-2 text-slate-400 hover:text-red-400 hover:bg-red-900/30 rounded-lg transition-colors"
                title="Delete player"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </div>
          </div>

          {/* Stats Row - Only shown if relevant settings enabled */}
          {(settings.showGraduationYear && player.graduationYear) && (
            <div data-element={`player-card-${player.id}-graduation`} className="flex items-center gap-4 text-sm text-slate-400 border-t border-slate-700 pt-3">
              <span>Class of {player.graduationYear}</span>
            </div>
          )}

          {/* Notes - Only shown if setting enabled */}
          {settings.showNotes && player.notes && (
            <div data-element={`player-card-${player.id}-notes`} className="mt-3 pt-3 border-t border-slate-700">
              <p className="text-sm text-slate-400 line-clamp-2">{player.notes}</p>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Swipe Actions */}
      <div 
        data-element={`player-card-${player.id}-swipe-actions`}
        className={`absolute right-0 top-0 bottom-0 flex items-center gap-1 pr-2 md:hidden transition-opacity duration-300 ${
          showActions ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      >
        <button
          data-element={`player-card-${player.id}-swipe-edit`}
          onClick={() => {
            onEdit(player);
            setShowActions(false);
          }}
          className="w-10 h-10 flex items-center justify-center bg-blue-500 text-white rounded-lg"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
          </svg>
        </button>
        <button
          data-element={`player-card-${player.id}-swipe-delete`}
          onClick={() => {
            onDelete(player);
            setShowActions(false);
          }}
          className="w-10 h-10 flex items-center justify-center bg-red-500 text-white rounded-lg"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default PlayerCard;
